import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Auth from "../Auth";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../sudokit/components/Dialog";
import { Input } from "../sudokit/components/Input";
import { SudoButton } from "../sudokit/components/SudoButton";
import { Text } from "../sudokit/components/Text";

type StripeMatch = {
  id: string;
  email: string | null;
  name: string | null;
  created: number | null;
  delinquent: boolean | null;
  hasSubscriptions: boolean;
  linkedToSudowriteUser: boolean;
};

type ExistingUser = {
  uid: string;
  email: string;
  displayName?: string;
  // Server-side flags so the modal can offer a recovery path for auth-only orphans
  // (Auth record exists but no corresponding userData / subscription).
  isOrphan: boolean;
  hasUserData: boolean;
  hasSubscription: boolean;
};

type LookupResult = {
  existingUser: ExistingUser | null;
  stripeMatches: StripeMatch[];
};

type AddCustomerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (result: { uid: string; email: string; stripeCustomerId: string }) => void;
};

type StripeChoice = { kind: "create-new" } | { kind: "use-existing"; customerId: string } | { kind: "paste-id"; customerId: string };

const formatCreated = (created: number | null) => {
  if (!created) return "—";
  return new Date(created * 1000).toLocaleDateString();
};

export default function AddCustomerModal({ open, onOpenChange, onCreated }: AddCustomerModalProps) {
  const [email, setEmail] = useState("");
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  const [choice, setChoice] = useState<StripeChoice>({ kind: "create-new" });
  const [pastedCustomerId, setPastedCustomerId] = useState("");

  const [allowEmailMismatch, setAllowEmailMismatch] = useState(false);
  const [mismatchPreview, setMismatchPreview] = useState<string | null>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<{ uid: string; email: string; stripeCustomerId: string } | null>(null);

  const [isDeletingOrphan, setIsDeletingOrphan] = useState(false);
  const [orphanDeleteError, setOrphanDeleteError] = useState<string | null>(null);

  // Bumped whenever the form is reset or the email is edited, so in-flight lookup/create
  // responses can detect they're stale and skip applying themselves. Also clears the
  // loading flags directly — the in-flight request's finally block intentionally won't
  // touch them on a stale session, so the invalidator has to.
  const sessionRef = useRef(0);
  const invalidateInFlight = () => {
    sessionRef.current += 1;
    setIsLookingUp(false);
    setIsCreating(false);
    setIsDeletingOrphan(false);
  };

  const reset = () => {
    invalidateInFlight();
    setEmail("");
    setLookupResult(null);
    setLookupError(null);
    setIsLookingUp(false);
    setChoice({ kind: "create-new" });
    setPastedCustomerId("");
    setAllowEmailMismatch(false);
    setMismatchPreview(null);
    setIsCreating(false);
    setCreateError(null);
    setCreateSuccess(null);
    setIsDeletingOrphan(false);
    setOrphanDeleteError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const runLookup = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setLookupError("Email is required");
      return;
    }
    setIsLookingUp(true);
    setLookupError(null);
    setLookupResult(null);
    setCreateError(null);
    setCreateSuccess(null);
    setMismatchPreview(null);
    setAllowEmailMismatch(false);
    const session = ++sessionRef.current;
    try {
      const res = await Auth.fetchWithIdToken("/api/admin/users/lookup-for-create", { email: trimmed });
      if (sessionRef.current !== session) return;
      if (res?.success === false) {
        setLookupError(res.message || "Lookup failed");
      } else {
        const matches: StripeMatch[] = res.stripeMatches || [];
        setLookupResult({ existingUser: res.existingUser, stripeMatches: matches });
        const preferred = matches.find((m) => !m.linkedToSudowriteUser);
        setChoice(preferred ? { kind: "use-existing", customerId: preferred.id } : { kind: "create-new" });
      }
    } catch (e: any) {
      if (sessionRef.current !== session) return;
      setLookupError(e?.message || "Lookup failed");
    } finally {
      if (sessionRef.current === session) setIsLookingUp(false);
    }
  };

  const submitCreate = async () => {
    if (!lookupResult || lookupResult.existingUser) return;
    setIsCreating(true);
    setCreateError(null);
    setMismatchPreview(null);

    let stripeCustomerId = "";
    if (choice.kind === "use-existing") stripeCustomerId = choice.customerId;
    if (choice.kind === "paste-id") {
      stripeCustomerId = pastedCustomerId.trim();
      if (!stripeCustomerId) {
        setCreateError("Paste a Stripe customer ID or switch back to create new.");
        setIsCreating(false);
        return;
      }
    }

    const session = ++sessionRef.current;
    try {
      const res = await Auth.fetchWithIdToken("/api/admin/users/create-with-stripe", {
        email: email.trim().toLowerCase(),
        stripeCustomerId: stripeCustomerId || undefined,
        allowEmailMismatch,
      });
      if (sessionRef.current !== session) return;
      if (res?.success === false && res.code === "STRIPE_EMAIL_MISMATCH") {
        setMismatchPreview(res.stripeCustomerEmail || "(no email on Stripe customer)");
        setCreateError(res.message);
      } else if (res?.success === false) {
        setCreateError(res.message || "Failed to create user");
      } else if (res?.success) {
        const summary = { uid: res.user.uid, email: res.user.email, stripeCustomerId: res.stripeCustomer.id };
        setCreateSuccess(summary);
        onCreated?.(summary);
      }
    } catch (e: any) {
      if (sessionRef.current !== session) return;
      setCreateError(e?.message || "Failed to create user");
    } finally {
      if (sessionRef.current === session) setIsCreating(false);
    }
  };

  const deleteOrphanAndRetry = async () => {
    if (!lookupResult?.existingUser?.isOrphan) return;
    const orphanUid = lookupResult.existingUser.uid;
    setIsDeletingOrphan(true);
    setOrphanDeleteError(null);
    const session = ++sessionRef.current;
    try {
      const res = await Auth.fetchWithIdToken("/api/admin/users/delete-orphan-auth", { uid: orphanUid });
      if (sessionRef.current !== session) return;
      if (res?.success === false) {
        setOrphanDeleteError(res.message || "Failed to delete orphan user");
        return;
      }
      // Clear the deleting flag before runLookup — runLookup bumps the session,
      // so the finally below would see a stale session and skip clearing it.
      setIsDeletingOrphan(false);
      // Re-run lookup so the modal updates to the empty-existingUser state and the
      // admin can proceed with create.
      await runLookup();
    } catch (e: any) {
      if (sessionRef.current !== session) return;
      setOrphanDeleteError(e?.message || "Failed to delete orphan user");
    } finally {
      if (sessionRef.current === session) setIsDeletingOrphan(false);
    }
  };

  // Switching the Stripe customer choice (or editing the pasted ID) invalidates any
  // mismatch warning that referred to the previous selection.
  const updateChoice = (next: StripeChoice) => {
    setChoice(next);
    setMismatchPreview(null);
    setAllowEmailMismatch(false);
  };

  const renderStripeOptions = () => {
    if (!lookupResult) return null;
    const { stripeMatches } = lookupResult;
    return (
      <div className="flex flex-col gap-4 mt-3">
        <Text size="sm" weight="medium">Stripe customer</Text>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name="stripe-choice"
            checked={choice.kind === "create-new"}
            onChange={() => updateChoice({ kind: "create-new" })}
          />
          <span>Create a new Stripe customer for this email</span>
        </label>
        {stripeMatches.map((match) => (
          <label key={match.id} className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="stripe-choice"
              checked={choice.kind === "use-existing" && choice.customerId === match.id}
              onChange={() => updateChoice({ kind: "use-existing", customerId: match.id })}
            />
            <div className="flex flex-col items-start">
              <a
                href={`https://dashboard.stripe.com/customers/${match.id}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono underline"
                onClick={(e) => e.stopPropagation()}
              >
                {match.id}
              </a>{" "}
              <Text as="span" size="sm" color="grey-1">
                ({match.email || "no email"} · created {formatCreated(match.created)}
                {match.hasSubscriptions ? " · has subscription" : ""}
                {match.linkedToSudowriteUser ? " · already linked to a Sudowrite user" : ""})
              </Text>
            </div>
          </label>
        ))}
        <label className="flex items-start gap-2 cursor-pointer mt-1">
          <input
            type="radio"
            name="stripe-choice"
            checked={choice.kind === "paste-id"}
            onChange={() => updateChoice({ kind: "paste-id", customerId: pastedCustomerId })}
          />
          <span>Paste a specific Stripe customer ID</span>
        </label>
        {choice.kind === "paste-id" && (
          <Input
            mode="default"
            placeholder="cus_..."
            value={pastedCustomerId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setPastedCustomerId(e.target.value);
              updateChoice({ kind: "paste-id", customerId: e.target.value });
            }}
          />
        )}
      </div>
    );
  };

  const canCreate =
    !!lookupResult &&
    !lookupResult.existingUser &&
    !isCreating &&
    (choice.kind !== "paste-id" || !!pastedCustomerId.trim()) &&
    (!mismatchPreview || allowEmailMismatch);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent aria-label="Add new customer">
        <DialogHeader>
          <DialogTitle>Add new customer</DialogTitle>
          <DialogDescription>Create a Sudowrite user and attach a Stripe customer.</DialogDescription>
        </DialogHeader>

        {createSuccess ? (
          <div className="flex flex-col gap-3 mt-4">
            <Text>
              Created user <span className="font-mono">{createSuccess.uid}</span> for {createSuccess.email}, linked to Stripe customer{" "}
              <span className="font-mono">{createSuccess.stripeCustomerId}</span>.
            </Text>
            <DialogFooter>
              <SudoButton variant="tertiaryGrey" onClick={reset}>Add another</SudoButton>
              <SudoButton variant="primary" onClick={() => handleOpenChange(false)}>Done</SudoButton>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-3 mt-4 w-full">
              <Text size="sm" weight="medium">Email</Text>
              <div className="flex gap-2">
                <Input
                  mode="default"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEmail(e.target.value);
                    // Lookup result + mismatch confirmation were tied to the previous email.
                    // Invalidate them (and any in-flight request) so the admin must
                    // re-run lookup before creating.
                    invalidateInFlight();
                    setLookupResult(null);
                    setLookupError(null);
                    setMismatchPreview(null);
                    setAllowEmailMismatch(false);
                    setCreateError(null);
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter" && !isLookingUp) runLookup();
                  }}
                  containerClassName="flex-1 mb-4"
                />
                <SudoButton variant="primary" onClick={runLookup} disabled={isLookingUp || !email.trim()}>
                  {isLookingUp ? "Searching..." : "Search"}
                </SudoButton>
              </div>
              {lookupError && <Text color="danger-color" size="sm">{lookupError}</Text>}
            </div>

            {lookupResult?.existingUser && !lookupResult.existingUser.isOrphan && (
              <div className="mt-3">
                <Text color="danger-color">
                  A Sudowrite user already exists for this email.{" "}<br/>
                  <Link
                    to={`/admin/user/${lookupResult.existingUser.email}`}
                    onClick={() => handleOpenChange(false)}
                    className="underline"
                  >
                    Open admin page for {lookupResult.existingUser.email}
                  </Link>
                </Text>
              </div>
            )}

            {lookupResult?.existingUser?.isOrphan && (
              <div className="mt-3 flex flex-col gap-2">
                <Text color="danger-color" size="sm">
                  A Firebase Auth record exists for this email (uid <span className="font-mono">{lookupResult.existingUser.uid}</span>) but
                  has no Sudowrite user data or active subscription. You can delete the auth-only record to free up the email,
                  then proceed with creation.
                </Text>
                <div>
                  <SudoButton
                    variant="danger"
                    onClick={deleteOrphanAndRetry}
                    disabled={isDeletingOrphan || isLookingUp}
                  >
                    {isDeletingOrphan ? "Deleting..." : "Delete orphan auth record"}
                  </SudoButton>
                </div>
                {orphanDeleteError && <Text color="danger-color" size="sm">{orphanDeleteError}</Text>}
              </div>
            )}

            {lookupResult && !lookupResult.existingUser && renderStripeOptions()}

            {mismatchPreview && (
              <div className="mt-3 flex flex-col gap-2">
                <Text color="danger-color" size="sm">
                  Stripe customer email is <span className="font-mono">{mismatchPreview}</span>, but you entered <span className="font-mono">{email}</span>.
                </Text>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={allowEmailMismatch} onChange={(e) => setAllowEmailMismatch(e.target.checked)} />
                  <span>Attach anyway (I confirm this is intentional)</span>
                </label>
              </div>
            )}

            {createError && !mismatchPreview && (
              <Text color="danger-color" size="sm" className="mt-3 block">{createError}</Text>
            )}

            <DialogFooter>
              <SudoButton variant="tertiaryGrey" onClick={() => handleOpenChange(false)}>Cancel</SudoButton>
              <SudoButton variant="primary" onClick={submitCreate} disabled={!canCreate}>
                {isCreating ? "Creating..." : "Create user"}
              </SudoButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
